import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { ContentfulImage } from "../../../components/_shared-components/image-related/ContentfulImage";
import { css } from "@emotion/react";
import React, { useRef } from "react";
import { SimpleAspectRatio } from "../../../components/_shared-components/SimpleAspectRatio";
import { mathRound } from "../../../utils/js/missing-js-parts";
import { CloseButtonSvg } from "../../../assets";
import { useLayoutEffectWithSSR } from "../../../hooks/use-layout-effect-with-ssr";
import { CSS_MQ } from "../../../styles/breakpoints";
import { useGlobalKeyListener } from "../../../hooks/use-global-key-listener";
import { VideoType } from "@swo/shared";
import { SwarovskiOptikVideoPlayer } from "../../../components/VideoComponent/VideoComponent";
import { useDeviceScreenHeightValue } from "../../../components/HeroImageComponent/use-device-screen-height-value";
import { fontColor } from "../../../styles/fonts";
import { PDPImagesGridImageT } from "../PDP_Gallery/types";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { ContentfulImage2 } from "../../../components/_shared-components/image-related/ContentfulImage2";
import type { TranslatedValue } from "@swo/storefront";
import { T } from "../../../components/T";

export const PDPLightbox: ReactTFC<{
  elements: NonEmptyArray<PDPImagesGridImageT | VideoType>;
  initiallyActiveIdx: number | undefined;
  isVisible: boolean;
  closeModal: () => void;
  closeLabel: TranslatedValue;
}> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const styleObj = useDeviceScreenHeightValue();

  useLayoutEffectWithSSR(() => {
    if (props.isVisible && typeof props.initiallyActiveIdx === "number") {
      const { current } = ref;

      if (current && current.children.length - 1 >= props.initiallyActiveIdx) {
        current.focus();

        const child = current.children.item(props.initiallyActiveIdx + 1);

        if (child) {
          child.scrollIntoView({ behavior: "auto" });
        }
      }
    }
  }, [props.isVisible]);

  useGlobalKeyListener("Escape", props.closeModal, [props.closeModal], true);

  return (
    <div
      css={css`
        position: relative;
        max-width: 100%;
        width: var(--max-container-width);

        margin-left: auto;
        margin-right: auto;

        padding-top: 20px;
        ${CSS_MQ.atLeastMd} {
          padding-top: 60px;
        }
        ${CSS_MQ.atLeastLg} {
          padding-top: 100px;
        }

        > .image-wrapper:not(:last-of-type) {
          margin-bottom: 50px;
        }
      `}
      ref={ref}
    >
      <button
        onClick={props.closeModal}
        css={css`
          position: fixed;

          right: 12px;
          top: 10px;
          ${CSS_MQ.atLeastMd} {
            right: 40px;
            top: 20px;
          }

          z-index: 2;

          &:hover {
            filter: opacity(0.9);
          }
        `}
      >
        <CloseButtonSvg
          css={css`
            background: black;
            color: ${CSS_COLORS.White};

            width: 50px;
            height: 50px;

            padding: 15px;
            border-radius: 50%;
          `}
        />

        <span
          css={css`
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            text-align: center;

            font-size: 10px;
            ${fontColor.black};
            letter-spacing: 0.5px;
            text-transform: uppercase;
          `}
        >
          <T>{props.closeLabel}</T>
        </span>
      </button>

      {props.elements.map(elem => {
        if (elem.__typename === "UtilityVideo") {
          return (
            <div
              key={elem.video.url}
              css={css`
                .contain-video {
                  height: var(--screen-height);
                  object-fit: contain;
                  background-color: ${CSS_COLORS.White};
                }
              `}
              style={styleObj}
            >
              <SwarovskiOptikVideoPlayer
                mode={"fill"}
                video={elem.video}
                subtitles={elem.subtitles}
                sys={{ id: "" }}
                portraitMode={false}
                withPlayerUI={false}
                resolutionPickerStrategy="ceil"
                videoClassName="contain-video"
                sharedVideoProps={{
                  muted: true,
                  playsInline: true,
                  autoPlay: true,
                  loop: true,
                  preload: "auto",
                }}
              />
            </div>
          );
        }
        return (
          <SimpleAspectRatio
            key={elem.url}
            className={"image-wrapper"}
            css={css`
              --aspect-ratio: ${mathRound(elem.height / elem.width, 3)};
            `}
          >
            <ContentfulImage2
              assetId={elem.sys.id}
              contentfulUrl={elem.url}
              cropping={undefined}
              withTransparency={true}
              sizes={"100vw"}
              alt={elem.description}
              loading="lazy"
              css={css`
                display: block;
                width: 100%;
                height: 100%;

                object-fit: contain;
                width: 100%;
              `}
            />
          </SimpleAspectRatio>
        );
      })}
    </div>
  );
};
